import type { Chip } from './chips';

/**
 * The beta stand-in for 4klabs.ai: the site's home page (front-door-landing/source.html, a copy of the built
 * page) with the prompt box in its hero, served at /fd-landing/ inside /landing on beta only. The copy loads none of
 * the site's analytics and has no demo form (that form posts to production); everything it shows comes from
 * 4klabs.ai. Each change must match exactly where the copy was taken, or the build stops: a new copy of the site
 * has to be checked again.
 */
export const SITE = 'https://4klabs.ai';

/** The files beside source.html that the page loads from its own folder. */
export const LANDING_FILES = ['reel.js', 'reel.css', 'nav.css', 'field.js', 'hero.css'] as const;

function once(html: string, pattern: RegExp, replacement: string, what: string): string {
  const found = html.match(new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`));
  if (found?.length !== 1) throw new Error(`front-door landing: expected one ${what} in source.html, found ${found?.length ?? 0}`);
  return html.replace(pattern, () => replacement);
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** What sending keeps, before anything is sent (privacy policy §13; the wording legal reviews). */
const NOTICE =
  'When you send this, we keep your words and the example you picked, even if you’ve declined analytics cookies. If you don’t create an account, we delete them after 90 days.';

const ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';

const chipButton = (c: Chip) =>
  `<button type="button" class="fd-chip" data-chip="${c.id}" data-cta="hero-chip-${c.id}" aria-pressed="false">` +
  `${c.kind === 'interactive' ? '<span class="fd-stop" aria-hidden="true"></span>' : ''}${escapeHtml(c.label)}</button>`;

function promptBox(chips: Chip[], script: string): string {
  const video = chips.filter((c) => c.kind === 'video').map(chipButton).join('');
  const interactive = chips.filter((c) => c.kind === 'interactive').map(chipButton).join('');
  return (
    '<form class="ctas fd-ask" id="hero-prompt" novalidate>' +
    '<div class="fd-box">' +
    '<textarea id="fd-words" name="words" rows="3" maxlength="4000" aria-label="Describe the video you want to make" placeholder="Describe the video you want to make…" data-clarity-mask="True"></textarea>' +
    `<div class="fd-bar"><span class="fd-hint">Free to start. You sign in on the next screen.</span><button type="submit" class="fd-go" aria-label="Start your video" data-cta="hero-prompt" disabled>${ARROW}</button></div>` +
    '</div>' +
    `<div class="fd-chips" role="group" aria-label="Start from an idea">${video}</div>` +
    `<div class="fd-chips fd-chips-ix" role="group" aria-label="Start an interactive video"><span class="fd-chips-label" aria-hidden="true">Interactive</span>${interactive}</div>` +
    // Hidden until the page around it says the capture is open: nothing it promises holds while the server drops the words.
    `<p class="fd-note" hidden>${NOTICE} <a href="/privacy" target="_top">Privacy policy</a></p>` +
    '<p class="fd-talk">Or <a href="#book" data-cta="hero-talk">talk to the team</a>.</p>' +
    '</form>' +
    // In the page, right here: the ideas show as soon as this much has arrived, and a script at the end of the page, or
    // one that has to be fetched, leaves them doing nothing for seconds on a slow connection.
    `<script type="application/json" id="fd-chips">${chipData(chips)}</script><script>${script}</script>`
  );
}

const chipData = (chips: Chip[]) =>
  JSON.stringify(chips.map(({ id, prompt }) => ({ id, prompt }))).replace(/</g, '\\u003c');

export function landingPage(source: string, chips: Chip[], script: string): string {
  let html = source;
  html = once(html, /<!doctype html>/i, '<!doctype html>\n<!-- Beta stand-in for 4klabs.ai, built from front-door-landing/source.html by src/lib/frontDoor/landingPage.ts. -->', 'doctype');
  html = once(html, /<meta name="robots" content="[^"]*">/, '<meta name="robots" content="noindex,nofollow">', 'robots tag');
  html = once(html, /<link rel="canonical"[^>]*>\n?/, '', 'canonical link');
  html = html.replace(/<script type="application\/ld\+json">[\s\S]*?<\/script>/g, '');
  html = once(html, /<script src="\/assets\/site\.js[^"]*"[^>]*><\/script>\n?/, '', 'site.js (analytics and consent)');
  html = once(html, /<script src="\/assets\/book\.js[^"]*"[^>]*><\/script>\n?/, '', 'book.js (the demo form, which posts to production)');
  html = once(
    html,
    /<div class="bookcard"><form id="bookform"[\s\S]*?<\/form><\/div>/,
    `<div class="bookcard"><a class="pill" href="${SITE}/#book" target="_blank" rel="noopener">Book a demo on 4klabs.ai</a></div>`,
    'demo form',
  );
  html = once(html, / · <a href="#" data-fk-consent>Cookie settings<\/a>/, '', 'cookie settings link');
  html = html.replace(/\/assets\/(reel\.js|reel\.css|nav\.css|field\.js)\?v=[0-9a-f]+/g, '$1');
  html = html.replace(/(["'\s(,])\/assets\//g, `$1${SITE}/assets/`);
  html = html.replace(/href="\/#([\w-]+)"/g, 'href="#$1"');
  html = html.replace(/href="\/"/g, 'href="/landing" target="_top"');
  html = html.replace(/href="\/(?!landing")([^"/][^"]*)"/g, `href="${SITE}/$1" target="_top"`);
  html = html.replace(/href="https:\/\/app\.4klabs\.ai\/(\?[^"]*)?"/g, 'href="/login$1" target="_top"');
  // After the links are pointed at 4klabs.ai: the box's own links (this app's privacy policy) stay as written.
  html = once(html, /<div class="ctas">[\s\S]*?<\/div>/, promptBox(chips, script), 'hero buttons');

  html = once(html, /<\/head>/, '<link rel="stylesheet" href="hero.css">\n</head>', 'head end');
  return html;
}
